import React, { useState, useEffect, useCallback } from 'react';
import {useAdminTheme} from '@/contexts/AdminThemeContext';import {Bell, Loader2, ChevronLeft, ChevronRight, Filter, Calendar, XCircle, Clock, Send, TrendingUp} from 'lucide-react';import {Button} from '@/components/ui/button';import AdminLayout from './AdminLayout';
import axios from 'axios';
import {API_URL as API} from '@/utils/apiConfig';
const TYPE_LABELS = {
  daily_reflection: 'Daily Reflection',
  recap: 'User Recap',
  recap_weekly: 'Weekly Recap',
  gift_subscription: 'Gift Subscription',
  subscription_expired: 'Subscription Expired',
  expiring_soon: 'Expiring Soon',
};

const TYPE_COLORS = {
  daily_reflection: { bg: '#14b8a620', text: '#14b8a6', dot: '#14b8a6' },
  recap: { bg: '#8b5cf620', text: '#8b5cf6', dot: '#8b5cf6' },
  recap_weekly: { bg: '#6366f120', text: '#6366f1', dot: '#6366f1' },
  gift_subscription: { bg: '#f59e0b20', text: '#f59e0b', dot: '#f59e0b' },
  subscription_expired: { bg: '#ef444420', text: '#ef4444', dot: '#ef4444' },
  expiring_soon: { bg: '#f9731620', text: '#f97316', dot: '#f97316' },
};

const RANGE_OPTIONS = [
  { value: 7, label: '7 days' },
  { value: 14, label: '14 days' },
  { value: 30, label: '30 days' },
  { value: 90, label: '90 days' },
];

function StatCard({ icon: Icon, label, value, sub, color, isDark, colors }) {
  return (
    <div className="rounded-xl p-4 flex items-center gap-3"
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}18` }}>
        <Icon className="h-5 w-5" style={{ color }} />
      </div>
      <div>
        <div className="text-xl font-bold" style={{ color: colors.text }}>{value}</div>
        <div className="text-xs" style={{ color: colors.textMuted }}>{label}</div>
        {sub && <div className="text-[11px] mt-0.5" style={{ color: colors.textMuted }}>{sub}</div>}
      </div>
    </div>
  );
}

function MiniBarChart({ daily, isDark, colors }) {
  if (!daily || daily.length === 0) return null;
  const max = Math.max(...daily.map(d => d.sent + d.failed), 1);

  return (
    <div className="rounded-xl p-4"
      style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}
      data-testid="daily-chart">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold" style={{ color: colors.text }}>Daily Delivery</h3>
        <div className="flex items-center gap-3 text-[11px]" style={{ color: colors.textMuted }}>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> Sent</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500 inline-block" /> Failed</span>
        </div>
      </div>
      <div className="flex items-end gap-1" style={{ height: 80 }}>
        {daily.map((d, i) => {
          const sentH = (d.sent / max) * 72;
          const failH = (d.failed / max) * 72;
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${d.date}: ${d.sent} sent, ${d.failed} failed`}>
              <div className="w-full flex flex-col items-center">
                {d.failed > 0 && <div className="w-full rounded-t" style={{ height: failH, background: '#ef4444', minHeight: d.failed > 0 ? 2 : 0 }} />}
                <div className="w-full rounded-t" style={{ height: sentH, background: '#10b981', minHeight: d.sent > 0 ? 2 : 0 }} />
              </div>
              {i % Math.max(1, Math.floor(daily.length / 7)) === 0 && (
                <span className="text-[9px] mt-1" style={{ color: colors.textMuted }}>
                  {d.date.slice(5)}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminNotificationLogPage() {
  const { isDark, colors } = useAdminTheme();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('');
  const [days, setDays] = useState(7);

  const fetchLogs = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 30, days });
      if (typeFilter) params.append('type', typeFilter);
      const res = await axios.get(`${API}/admin/notification-log?${params}`, { withCredentials: true });
      setLogs(res.data.notifications || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch { /* ignore */ }
    setLoading(false);
  }, [typeFilter, days]);

  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/notification-log/stats?days=${days}`, { withCredentials: true });
      setStats(res.data);
    } catch { /* ignore */ }
    setStatsLoading(false);
  }, [days]);

  useEffect(() => { fetchLogs(1); fetchStats(); }, [fetchLogs, fetchStats]);

  const totalPages = Math.max(1, Math.ceil(total / 30));

  const formatTime = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
  };

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-notification-log-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold" style={{ color: colors.text }}>Notification Log</h1>
            <p className="text-sm mt-1" style={{ color: colors.textMuted }}>Track all push notification deliveries</p>
          </div>
          <div className="flex items-center gap-2">
            {RANGE_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setDays(opt.value)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg transition-colors"
                style={{
                  background: days === opt.value ? colors.accent : (isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'),
                  color: days === opt.value ? '#fff' : colors.textMuted,
                }}
                data-testid={`range-${opt.value}d`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Stats Cards */}
        {statsLoading ? (
          <div className="text-center py-8"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></div>
        ) : stats && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatCard icon={Send} label="Total Sent" value={stats.total_sent} sub={`Last ${days} days`} color="#10b981" isDark={isDark} colors={colors} />
              <StatCard icon={XCircle} label="Total Failed" value={stats.total_failed} sub={stats.total_sent > 0 ? `${((stats.total_failed / (stats.total_sent + stats.total_failed)) * 100).toFixed(1)}% failure rate` : ''} color="#ef4444" isDark={isDark} colors={colors} />
              <StatCard icon={TrendingUp} label="Types Active" value={Object.keys(stats.by_type).length} sub="Notification types" color="#6366f1" isDark={isDark} colors={colors} />
              <StatCard icon={Calendar} label="Days Tracked" value={days} sub={stats.daily?.length ? `${stats.daily.length} days with data` : 'No data'} color="#f59e0b" isDark={isDark} colors={colors} />
            </div>

            {/* Chart */}
            <MiniBarChart daily={stats.daily} isDark={isDark} colors={colors} />

            {/* Type breakdown */}
            {Object.keys(stats.by_type).length > 0 && (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
                {Object.entries(stats.by_type).map(([type, data]) => {
                  const tc = TYPE_COLORS[type] || { bg: '#64748b20', text: '#64748b', dot: '#64748b' };
                  return (
                    <button
                      key={type}
                      onClick={() => setTypeFilter(typeFilter === type ? '' : type)}
                      className="rounded-lg p-3 text-left transition-all"
                      style={{
                        background: typeFilter === type ? tc.bg : (isDark ? 'rgba(30,41,59,0.3)' : '#f8fafc'),
                        border: `1px solid ${typeFilter === type ? tc.dot : colors.cardBorder}`,
                      }}
                      data-testid={`type-filter-${type}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-2 h-2 rounded-full" style={{ background: tc.dot }} />
                        <span className="text-xs font-medium" style={{ color: tc.text }}>{data.label}</span>
                      </div>
                      <div className="text-lg font-bold" style={{ color: colors.text }}>{data.total_sent}</div>
                      <div className="text-[11px]" style={{ color: colors.textMuted }}>
                        {data.count} events &middot; {data.total_failed} failed
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Filter Bar */}
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            <Filter className="h-3.5 w-3.5" style={{ color: colors.textMuted }} />
            <span className="text-xs font-medium" style={{ color: colors.textMuted }}>Filter:</span>
          </div>
          <button
            onClick={() => setTypeFilter('')}
            className="px-2.5 py-1 text-xs rounded-lg transition-colors"
            style={{
              background: !typeFilter ? colors.accent : 'transparent',
              color: !typeFilter ? '#fff' : colors.textMuted,
              border: `1px solid ${!typeFilter ? colors.accent : colors.cardBorder}`,
            }}
            data-testid="filter-all"
          >
            All
          </button>
          {Object.entries(TYPE_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTypeFilter(typeFilter === key ? '' : key)}
              className="px-2.5 py-1 text-xs rounded-lg transition-colors"
              style={{
                background: typeFilter === key ? (TYPE_COLORS[key]?.bg || colors.accent) : 'transparent',
                color: typeFilter === key ? (TYPE_COLORS[key]?.text || '#fff') : colors.textMuted,
                border: `1px solid ${typeFilter === key ? (TYPE_COLORS[key]?.dot || colors.accent) : colors.cardBorder}`,
              }}
              data-testid={`filter-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Log Table */}
        <div className="rounded-2xl overflow-hidden" style={{ background: isDark ? 'rgba(30,41,59,0.5)' : '#fff', border: `1px solid ${colors.cardBorder}` }}>
          {loading ? (
            <div className="text-center py-16"><Loader2 className="h-5 w-5 animate-spin mx-auto" style={{ color: colors.textMuted }} /></div>
          ) : logs.length === 0 ? (
            <div className="text-center py-16" data-testid="empty-state">
              <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" style={{ color: colors.textMuted }} />
              <p className="text-sm" style={{ color: colors.textMuted }}>No notifications in this period</p>
              <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Notifications will appear here once they're sent</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" style={{ color: colors.text }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${colors.cardBorder}` }}>
                    <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Time</th>
                    <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Type</th>
                    <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Date Key</th>
                    <th className="text-right px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Sent</th>
                    <th className="text-right px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Failed</th>
                    <th className="text-left px-4 py-3 font-medium text-xs" style={{ color: colors.textMuted }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log, i) => {
                    const tc = TYPE_COLORS[log.type] || { bg: '#64748b20', text: '#64748b', dot: '#64748b' };
                    return (
                      <tr key={i} style={{ borderBottom: `1px solid ${colors.cardBorder}` }} data-testid={`notif-row-${i}`}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3 shrink-0" style={{ color: colors.textMuted }} />
                            <span className="text-xs" style={{ color: colors.textMuted }}>{formatTime(log.sent_at)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium"
                            style={{ background: tc.bg, color: tc.text }}>
                            <span className="w-1.5 h-1.5 rounded-full" style={{ background: tc.dot }} />
                            {TYPE_LABELS[log.type] || log.type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs font-mono" style={{ color: colors.textMuted }}>
                          {log.date_key || '—'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-medium" style={{ color: '#10b981' }}>
                            {log.sent_count ?? (log.user_id ? 1 : '—')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <span className="text-xs font-medium" style={{ color: (log.failed_count || 0) > 0 ? '#ef4444' : colors.textMuted }}>
                            {log.failed_count ?? 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs" style={{ color: colors.textMuted }}>
                          {log.user_id && <span>User: {log.user_id.slice(0, 12)}...</span>}
                          {log.details && (
                            <span>
                              {log.details.tier && ` Tier: ${log.details.tier}`}
                              {log.details.days && ` (${log.details.days}d)`}
                              {log.details.is_trial && ' [Trial]'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3">
            <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => fetchLogs(page - 1)}
              data-testid="prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm" style={{ color: colors.textMuted }}>Page {page} of {totalPages}</span>
            <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => fetchLogs(page + 1)}
              data-testid="next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
